import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Video, ArrowRight, Camera, Film, Home } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import NicheComTag from "../components/NicheComTag";
import useApi from "../hooks/useApi";
import { useBranch } from "../context/BranchContext";

function Ring({ pct, size = 128 }) {
  const r = size / 2 - 10, c = 2 * Math.PI * r, off = c * (1 - pct / 100);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDEEE9" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#93C020" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 1.1s cubic-bezier(.16,1,.3,1)" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center font-extrabold tabular-nums" style={{ fontSize: size * 0.24, color: "#16180F" }} data-testid="nichecom-overall-pct">{pct}%</div>
    </div>
  );
}

export default function NicheComUsage() {
  const navigate = useNavigate();
  const { branchId } = useBranch();
  const { data } = useApi(`/growth/nichecom-usage?branch_id=${branchId}`, [branchId]);
  if (!data) return <><TopBar /><div className="p-6 sm:p-8 max-w-[1200px]">
    <div className="mb-6 flex items-center gap-2 animate-rise">
      <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Your NicheCom Usage</h1><NicheComTag />
    </div>
    <div className="h-64 rounded-xl animate-pulse" style={{ background: "#EDEEE9" }} />
  </div></>;

  return (
    <>
      <TopBar />
      <div className="p-6 sm:p-8 max-w-[1200px]">
        <div className="mb-6 flex items-center gap-2 animate-rise">
          <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Your NicheCom Usage</h1>
          <NicheComTag />
        </div>

        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 card p-8 flex items-center gap-6" data-testid="nichecom-hero">
            <Ring pct={data.overall} />
            <div>
              <div className="label-caps">Overall utilisation</div>
              <p className="text-ink-2 text-sm mt-2 leading-relaxed">{data.assets_unused} available assets haven't been used yet.</p>
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold" style={{ background: "rgba(147,192,32,.12)", color: "#5F8213" }}>
                Potential Growth Score: +{data.potential_growth_gain}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 card p-6">
            <div className="label-caps mb-4">By category</div>
            <div className="space-y-3.5">
              {data.categories.map((c) => (
                <div key={c.key} data-testid={`category-${c.key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-ink-2">{c.label}</span>
                    <span className="font-bold text-ink tabular-nums">{c.used}%</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${c.used}%`, background: c.used >= 70 ? "#93C020" : c.used >= 50 ? "#B7CE5E" : "#E5A93D" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Video opportunity */}
        <div className="mt-8 card p-6 sm:p-8" data-testid="video-opportunity" style={{ background: "linear-gradient(135deg, #ffffff 0%, #F6FAEE 100%)" }}>
          <div className="flex items-center gap-3 mb-5">
            <span className="grid place-items-center w-11 h-11 rounded-xl" style={{ background: "var(--grad-green)" }}><Video size={22} strokeWidth={1.75} color="#fff" /></span>
            <div>
              <div className="flex items-center gap-2"><h2 className="text-xl font-bold tracking-tight text-ink">Your video opportunity</h2><NicheComTag /></div>
              <p className="text-ink-2 text-sm">You already have the content — it just hasn't been published.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[["Videos created", data.video.created, Film], ["Published", data.video.published, Video], ["Unused", data.video.unused, Home], ["Utilisation", `${data.video.utilisation}%`, Camera]].map(([k, v, Ic], i) => (
              <div key={k} className="rounded-xl p-4 bg-white border" style={{ borderColor: "#E7E8E4" }}>
                <Ic size={16} strokeWidth={1.75} style={{ color: i === 2 ? "#D95C5C" : "#5F8213" }} />
                <div className="text-2xl font-extrabold tracking-tighter text-ink mt-2">{v}</div>
                <div className="text-[11px] text-ink-3">{k}</div>
              </div>
            ))}
          </div>

          <p className="text-sm text-ink mb-4">
            <span className="font-semibold">You're leaving professional video unused.</span>{" "}
            {data.video.unused} professionally produced property videos haven't yet been published across your social channels.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {data.video.properties.map((p, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl bg-white border p-3" style={{ borderColor: "#E7E8E4" }} data-testid={`unused-video-${i}`}>
                <span className="grid place-items-center w-9 h-9 rounded-lg shrink-0" style={{ background: "var(--grad-green-soft)" }}><Video size={15} strokeWidth={1.75} style={{ color: "#5F8213" }} /></span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink truncate">{p.address}</div>
                  <div className="text-[11px] text-ink-3 truncate">{p.type} · {p.asset}</div>
                </div>
              </div>
            ))}
          </div>

          <button className="btn-primary focus-ring" data-testid="use-my-videos" onClick={() => navigate("/app/social-week")}>
            Use my videos <ArrowRight size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </>
  );
}
